import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { canonicalTransactionRowSchema, transactionRowSchema, type CanonicalTransactionRow, type TransactionRow } from './useTransactions';

const canonicalIdentityFields = [
  'canonical_transaction_id',
  'representative_transaction_id',
  'canonical_account_id',
  'physical_source_transaction_ids',
  'annotation_anchor_transaction_id',
  'annotation_state',
  'tombstone',
] as const;

const linkCandidateLimitSchema = z.number().finite().int().min(1).max(200);

type LinkCandidateLeg = CanonicalTransactionRow | TransactionRow;

function hasCanonicalIdentity(value: unknown): boolean {
  return Boolean(value && typeof value === 'object' && canonicalIdentityFields.some((field) => Object.prototype.hasOwnProperty.call(value, field)));
}

function parseLinkCandidateLeg(value: unknown): LinkCandidateLeg {
  return hasCanonicalIdentity(value)
    ? canonicalTransactionRowSchema.parse(value)
    : transactionRowSchema.parse(value);
}

const linkCandidateInputSchema = z.object({
  candidate_id: z.string().min(1),
  confidence: z.number().nullable(),
  tolerance_applied: z.string(),
  reason: z.string().nullable(),
  legs: z.tuple([z.unknown(), z.unknown()]),
}).strict();

export const linkCandidateSchema = linkCandidateInputSchema.transform((candidate, context) => {
  const parsedLegs: LinkCandidateLeg[] = [];
  let valid = true;
  candidate.legs.forEach((leg, index) => {
    try {
      parsedLegs.push(parseLinkCandidateLeg(leg));
    } catch {
      valid = false;
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['legs', index], message: 'invalid link candidate leg' });
    }
  });
  if (!valid) return z.NEVER;
  return { ...candidate, legs: parsedLegs as [LinkCandidateLeg, LinkCandidateLeg] };
});

export type LinkCandidate = z.infer<typeof linkCandidateSchema>;

const linkCandidatesResponseSchema = z.object({
  data: z.array(linkCandidateSchema),
  meta: z.object({}).strict(),
}).strict();

export function parseLinkCandidatesResponse(payload: unknown): LinkCandidate[] {
  return linkCandidatesResponseSchema.parse(payload).data;
}

export function useLinkCandidates(enabled = true, limit = 50) {
  return useQuery({
    queryKey: ['transactions', 'link-candidates', { limit }],
    enabled,
    queryFn: async () => {
      const parsedLimit = linkCandidateLimitSchema.safeParse(limit);
      if (!parsedLimit.success) {
        throw new Error('link candidate limit must be an integer between 1 and 200');
      }
      const response = await client.get('/api/transactions/link-candidates', { params: { limit: parsedLimit.data } });
      return parseLinkCandidatesResponse(response.data);
    },
  });
}
